import React, { useState, useEffect, useCallback } from 'react';
import { scoringCategories } from '../data/scoringCategories';
import { searchClient } from '../utils/algolia';
import { X } from 'lucide-react';

const CalculatorView = () => {
  const [scores, setScores] = useState({});
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const calculateCategoryScore = (category) => {
    const categoryData = scoringCategories[category];
    let totalPoints = 0;
    let maxPoints = 0;

    categoryData.attributes.forEach(attr => {
      const selectedValue = scores[attr.name];
      if (selectedValue !== undefined) {
        totalPoints += attr.points[selectedValue] || 0;
      }
      maxPoints += Math.max(...Object.values(attr.points));
    });

    const percentage = maxPoints > 0 ? (totalPoints / maxPoints) : 0;
    return Math.round(percentage * categoryData.weight);
  };

  const calculateTotalScore = () => {
    return Object.keys(scoringCategories).reduce((total, category) => {
      return total + calculateCategoryScore(category);
    }, 0);
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    if (score >= 25) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreLabel = (score) => {
    if (score >= 75) return "Hot Lead";
    if (score >= 50) return "Warm Lead";
    if (score >= 25) return "Cold Lead";
    return "Unqualified";
  };

  const handleChange = (attrName, value) => {
    setScores(prev => ({
      ...prev,
      [attrName]: value
    }));
  };

  const resetScores = () => {
    setScores({});
    setMatchingCompanies([]);
    setShowMatches(false);
  };

  // Map calculator attributes to Algolia index attributes and normalize values
  const mapAttributeToAlgolia = (attrName, value) => {
    const mapping = {
      companySize: 'company_size',
      revenue: 'revenue',
      timeline: 'timeline',
      budget: 'budget',
      demoRequest: 'demo_request',
    };
    
    if (!mapping[attrName]) {
      return null;
    }
    
    const algoliaField = mapping[attrName];
    let normalizedValue = value;
    
    // Normalize values to match Algolia data format
    if (attrName === 'demoRequest') {
      normalizedValue = value === 'Yes' ? 'Yes' : 'No';
    } else if (attrName === 'budget') {
      // Map budget values - these should match exactly what's in Algolia
      // "Budget allocated" should match "Budget allocated" in Algolia
      normalizedValue = value;
    } else if (attrName === 'timeline') {
      // Timeline values should match exactly
      normalizedValue = value;
    } else if (attrName === 'companySize') {
      // Company size might need normalization
      normalizedValue = value;
    } else if (attrName === 'revenue') {
      // Revenue values should match exactly
      normalizedValue = value;
    }
    
    return { [algoliaField]: normalizedValue };
  };

  // Search Algolia for matching companies
  const searchMatchingCompanies = useCallback(async (currentScores) => {
    if (Object.keys(currentScores).length === 0) {
      setMatchingCompanies([]);
      setShowMatches(false);
      return;
    }

    setIsSearching(true);
    try {
      const filters = [];
      
      // Build filters from selected scores
      Object.entries(currentScores).forEach(([attrName, value]) => {
        if (value) {
          const algoliaFilter = mapAttributeToAlgolia(attrName, value);
          if (algoliaFilter) {
            const [key, val] = Object.entries(algoliaFilter)[0];
            // Algolia filter format: attribute:"value" (no escaping needed for quotes in values)
            // But we need to handle special characters
            const filterValue = String(val).trim();
            filters.push(`${key}:"${filterValue}"`);
            console.log(`Added filter: ${key}:"${filterValue}"`);
          }
        }
      });

      if (filters.length === 0) {
        setMatchingCompanies([]);
        setShowMatches(false);
        setIsSearching(false);
        return;
      }

      // First, let's try to get some sample data to see what values are actually in Algolia
      const testSearchResponse = await searchClient.search([
        {
          indexName: 'leads',
          query: '',
          params: {
            hitsPerPage: 5,
            attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
          },
        },
      ]);

      if (testSearchResponse?.results?.[0]?.hits?.length > 0) {
        console.log('Sample Algolia data:', testSearchResponse.results[0].hits[0]);
        console.log('Sample company_size:', testSearchResponse.results[0].hits[0].company_size);
        console.log('Sample revenue:', testSearchResponse.results[0].hits[0].revenue);
        console.log('Sample timeline:', testSearchResponse.results[0].hits[0].timeline);
        console.log('Sample budget:', testSearchResponse.results[0].hits[0].budget);
        console.log('Sample demo_request:', testSearchResponse.results[0].hits[0].demo_request);
      }

      const filterString = filters.join(' AND ');
      console.log('Searching Algolia with filters:', filterString);
      console.log('Current scores:', currentScores);
      console.log('Filter array:', filters);
      
      // Try using facetFilters instead of filters (for facetable attributes)
      const facetFilters = filters.map(filter => {
        // Convert "company_size:"201-1000"" to ["company_size:201-1000"]
        const match = filter.match(/(\w+):"(.+)"/);
        if (match) {
          return `${match[1]}:${match[2]}`;
        }
        return filter;
      });
      
      console.log('Trying facetFilters:', facetFilters);
      
      // First try with regular filters
      let searchResponse = await searchClient.search([
        {
          indexName: 'leads',
          query: '',
          params: {
            filters: filterString,
            hitsPerPage: 10,
            attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
          },
        },
      ]);
      
      // If no results, try with facetFilters
      if (!searchResponse?.results?.[0]?.hits || searchResponse.results[0].hits.length === 0) {
        console.log('No results with filters, trying facetFilters...');
        searchResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              facetFilters: [facetFilters],
              hitsPerPage: 10,
              attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
            },
          },
        ]);
      }
      
      // If still no results, try client-side filtering
      if (!searchResponse?.results?.[0]?.hits || searchResponse.results[0].hits.length === 0) {
        console.log('No results with facetFilters, trying client-side filtering...');
        // Get all results and filter client-side
        const allResultsResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              hitsPerPage: 1000, // Get more results to filter client-side
              attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
            },
          },
        ]);
        
        const allHits = allResultsResponse?.results?.[0]?.hits || [];
        console.log('Total hits to filter:', allHits.length);
        
        // Filter client-side
        const filteredHits = allHits.filter(hit => {
          return filters.every(filter => {
            const match = filter.match(/(\w+):"(.+)"/);
            if (match) {
              const field = match[1];
              const value = match[2];
              const hitValue = hit[field];
              console.log(`Comparing ${field}: hit="${hitValue}" vs filter="${value}"`);
              return hitValue === value;
            }
            return false;
          });
        }).map(hit => {
          // Ensure all required fields are present
          return {
            ...hit,
            company_name: hit.company_name || hit.companyName || hit.name || 'Unknown Company',
            total_score: hit.total_score || hit.totalScore || 0,
            lead_tier: hit.lead_tier || hit.leadTier || null,
          };
        });
        
        console.log('Client-side filtered hits:', filteredHits.length);
        console.log('Sample filtered hit:', filteredHits[0]);
        
        if (filteredHits.length > 0) {
          // Log the first hit to see its structure
          if (filteredHits[0]) {
            console.log('First hit keys:', Object.keys(filteredHits[0]));
            console.log('First hit company_name:', filteredHits[0].company_name);
            console.log('First hit object:', filteredHits[0]);
          }
          setMatchingCompanies(filteredHits.slice(0, 10)); // Limit to 10
          setShowMatches(true);
          setIsSearching(false);
          return;
        }
      }

      console.log('Full Algolia search response:', searchResponse);
      console.log('Results array:', searchResponse?.results);

      const hits = searchResponse?.results?.[0]?.hits || [];
      console.log('Extracted hits:', hits);
      console.log('Number of hits:', hits.length);
      
      // If no exact matches, try a more flexible search with OR conditions
      if (hits.length === 0 && filters.length > 0) {
        console.log('No exact matches found, trying flexible search...');
        // Try searching with individual filters using OR instead of AND
        const flexibleFilters = filters.map(filter => `(${filter})`).join(' OR ');
        console.log('Trying flexible filters:', flexibleFilters);
        
        const flexibleResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              filters: flexibleFilters,
              hitsPerPage: 10,
              attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
            },
          },
        ]);
        
        const flexibleHits = flexibleResponse?.results?.[0]?.hits || [];
        console.log('Flexible search hits:', flexibleHits.length);
        
        if (flexibleHits.length > 0) {
          // Filter results client-side to match at least one criteria
          const matchedHits = flexibleHits.filter(hit => {
            return filters.some(filter => {
              const [field, value] = filter.split(':');
              const cleanField = field.replace(/"/g, '');
              const cleanValue = value.replace(/"/g, '').replace(/\\/g, '');
              return hit[cleanField] === cleanValue;
            });
          });
          
          if (matchedHits.length > 0) {
            console.log('Found matches with flexible search:', matchedHits.length);
            setMatchingCompanies(matchedHits);
            setShowMatches(true);
            setIsSearching(false);
            return;
          }
        }
      }

      if (hits.length > 0) {
        console.log('Found companies:', hits.length);
        console.log('Sample hit from regular search:', hits[0]);
        console.log('Sample hit keys:', hits[0] ? Object.keys(hits[0]) : 'No hits');
        setMatchingCompanies(hits);
        setShowMatches(true);
      } else {
        console.log('No companies found with exact filters');
        
        // Try a search without filters to verify connection works
        const testConnectionResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              hitsPerPage: 1,
              attributesToRetrieve: ['company_name', 'total_score'],
            },
          },
        ]);
        
        const testHits = testConnectionResponse?.results?.[0]?.hits || [];
        if (testHits.length > 0) {
          console.log('Connection works - found', testHits.length, 'companies without filters');
          console.log('This means the filter values might not match the data in Algolia');
        } else {
          console.log('No companies found even without filters - check Algolia index');
        }
        
        setMatchingCompanies([]);
        // Still show the panel even if no results, so user knows search ran
        if (filters.length > 0) {
          setShowMatches(true);
        }
      }
    } catch (error) {
      console.error('Error searching Algolia:', error);
      setMatchingCompanies([]);
      setShowMatches(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Watch for changes in scores and trigger search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchMatchingCompanies(scores);
    }, 500); // Debounce search by 500ms

    return () => clearTimeout(timeoutId);
  }, [scores, searchMatchingCompanies]);

  const totalScore = calculateTotalScore();

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Matching Companies Panel - Top Right */}
      {showMatches && (
        <div className="fixed top-20 right-4 w-80 md:w-96 max-h-[calc(100vh-6rem)] bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Matching Companies ({matchingCompanies.length})
            </h3>
            <button
              onClick={() => setShowMatches(false)}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 p-4">
            {isSearching ? (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-pulse">Searching...</div>
              </div>
            ) : matchingCompanies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No matching companies found</p>
                <p className="text-xs text-gray-400 mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {matchingCompanies.map((company, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {company.company_name || company.companyName || company.name || 'Unknown Company'}
                      </h4>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        company.total_score >= 75 ? 'bg-green-100 text-green-800' :
                        company.total_score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        company.total_score >= 25 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {company.total_score}
                      </div>
                    </div>
                    {company.lead_tier && (
                      <div className="text-xs text-gray-600">{company.lead_tier}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Lead Score Calculator</h1>
              <button
                onClick={resetScores}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reset All
              </button>
            </div>
            {Object.keys(scores).length > 0 && (
              <button
                onClick={() => setShowMatches(!showMatches)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <span>Matching Companies</span>
                {isSearching ? (
                  <span className="bg-blue-500 px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                    ...
                  </span>
                ) : (
                  <span className="bg-blue-500 px-2 py-1 rounded-full text-xs font-bold">
                    {matchingCompanies.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Total Score</h2>
              <div className="text-center">
                <div className={`text-6xl font-bold mb-2 ${getScoreColor(totalScore)}`}>
                  {totalScore}
                </div>
                <div className="text-lg text-gray-600 mb-4">
                  {getScoreLabel(totalScore)}
                </div>
                <div className="text-sm text-gray-500">out of 100 points</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Score Legend</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-600 rounded"></div>
                  <span className="text-sm">Unqualified (0-24)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-600 rounded"></div>
                  <span className="text-sm">Cold (25-49)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-sm">Warm (50-74)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-600 rounded"></div>
                  <span className="text-sm">Hot (75-100)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Category Scores</h2>
              <div className="space-y-4">
                {Object.entries(scoringCategories).map(([key, category]) => {
                  const Icon = category.icon;
                  const categoryScore = calculateCategoryScore(key);
                  return (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">{category.title}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{categoryScore}</div>
                        <div className="text-sm text-gray-500">of {category.weight}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {Object.entries(scoringCategories).map(([categoryKey, category]) => {
            const Icon = category.icon;
            return (
              <div key={categoryKey} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-6 h-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold">{category.title}</h3>
                    <p className="text-sm text-gray-600">Weight: {category.weight} points</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {category.attributes.map(attr => (
                    <div key={attr.name} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {attr.label}
                      </label>
                      <select
                        value={scores[attr.name] || ''}
                        onChange={(e) => handleChange(attr.name, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select...</option>
                        {Object.entries(attr.points).map(([option, points]) => (
                          <option key={option} value={option}>
                            {option} ({points} pts)
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4">Scoring Guidelines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-green-800">Hot Lead (75-100)</h3>
              <p className="text-sm text-green-700">Immediate sales follow-up required.</p>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h3 className="font-semibold text-yellow-800">Warm Lead (50-74)</h3>
              <p className="text-sm text-yellow-700">Qualified prospect. Schedule within 48h.</p>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-orange-800">Cold Lead (25-49)</h3>
              <p className="text-sm text-orange-700">Add to nurture campaign.</p>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h3 className="font-semibold text-red-800">Unqualified (&lt;25)</h3>
              <p className="text-sm text-red-700">Low priority or disqualify.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CalculatorView;